// src/pages/Dashboard/components/MonthlyTrends.jsx
import React, { memo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import ChartCard from "./ChartCard";
import { COLORS } from "../constants";

const CHART_HEIGHT = 300;

/**
 * All time-series / single-project charts driven by month-grouped data.
 * Kept in one file so the section can be lazy-loaded as a unit if
 * needed later (React.lazy/Suspense friendly).
 */
function MonthlyTrends({ monthlyData, injuryData, nonInjuryData, formatter }) {
  const tooltipFormatter = (v) => formatter.format(v);

  return (
    <>
      <ChartCard title="1. Average Man Hours Worked">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Line
              type="monotone"
              dataKey="totalManHoursWorked"
              stroke="#2563eb"
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="2. Average Manpower">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Area
              type="monotone"
              dataKey="avgManpower"
              stroke="#10b981"
              fill="#10b98133"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="3. FR Trend">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Line type="monotone" dataKey="fr" stroke="#ef4444" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="4. SR Trend">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Line type="monotone" dataKey="sr" stroke="#f59e0b" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="5. Safety Meetings">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Bar
              dataKey="safetyCommitteeMeetings"
              name="Committee Meetings"
              fill="#2563eb"
            />
            <Bar dataKey="tbtMeetingHours" name="TBT Hours" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="6. Safety Inductions Done">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Area
              type="monotone"
              dataKey="safetyInductionConducted"
              stroke="#8b5cf6"
              fill="#8b5cf633"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="7. Internal vs External Training">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Bar
              dataKey="internalTraining"
              name="Internal"
              stackId="a"
              fill="#2563eb"
            />
            <Bar
              dataKey="externalTraining"
              name="External"
              stackId="a"
              fill="#10b981"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="8. Safety Audits Done">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Bar dataKey="internalAudits" name="Internal" fill="#2563eb" />
            <Bar dataKey="externalAudits" name="External" fill="#10b981" />
            <Bar
              dataKey="hseAuditInternal"
              name="HSE Internal"
              fill="#f59e0b"
            />
            <Bar
              dataKey="hseAuditExternal"
              name="HSE External"
              fill="#ef4444"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="9. Injury Incident Breakdown">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart>
            <Pie
              data={injuryData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={95}
              label={({ name, value }) => `${name}: ${value}`}
            >
              {injuryData.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>

            <Tooltip formatter={tooltipFormatter} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="10. Non-Injury Incident Trend">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <AreaChart data={nonInjuryData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Area
              type="monotone"
              dataKey="Near Miss Incidents"
              stackId="1"
              stroke="#2563eb"
              fill="#2563eb55"
            />
            <Area
              type="monotone"
              dataKey="Dangerous Occurrences"
              stackId="1"
              stroke="#ef4444"
              fill="#ef444455"
            />
            <Area
              type="monotone"
              dataKey="Other Incidents"
              stackId="1"
              stroke="#f59e0b"
              fill="#f59e0b55"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="11. ESG: Water & Waste">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Bar
              dataKey="waterConsumption"
              name="Water Consumption"
              fill="#2563eb"
            />
            <Bar dataKey="waterRecycled" name="Water Recycled" fill="#10b981" />
            <Bar
              dataKey="hazardousWasteGenerated"
              name="Hazardous Waste"
              fill="#ef4444"
            />
            <Bar
              dataKey="nonHazardousWasteGenerated"
              name="Non-Hazardous Waste"
              fill="#f59e0b"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="12. NCR / NCN / UA-UC Closure">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
            <Bar dataKey="internalNCRs" name="Internal NCRs" fill="#ef4444" />
            <Bar dataKey="openNCRs" name="Open NCRs" fill="#f59e0b" />
            <Bar dataKey="openNCNs" name="Open NCNs" fill="#8b5cf6" />
            <Bar
              dataKey="uaUcReportedClosed"
              name="UA/UC Closed"
              fill="#10b981"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}

export default memo(MonthlyTrends);
